/**
 * Email verification, as the server and the browser both have to read it.
 *
 * Three things live here because two halves of the product would otherwise each invent their
 * own: the sentence a player is shown when an unverified account is refused at the door, the
 * predicate the client uses to recognise that refusal (and offer the resend instead of leaving
 * somebody re-typing a password that was right), and the path the link in the mail points at.
 *
 * THE SENTENCE IS THE ERROR CODE. tRPC carries a code (`FORBIDDEN`, here) and a message, and
 * nothing else that survives the wire without a custom serializer — so the message *is* the
 * discriminator, and `isEmailUnverifiedMessage` is the one reading of it. That is the same
 * arrangement a ban is under (`banMessage`), which the client also renders verbatim; the
 * difference is that this one has an affordance attached to it, so it has to be recognised
 * rather than only displayed.
 */

/**
 * What a player is told when their password was right and their address is not confirmed.
 *
 * FORBIDDEN, not "invalid email or password": someone who got this far has already proved the
 * account is theirs, so the anonymity `INVALID_CREDENTIALS` protects has nothing left to
 * protect and a wrong-password message would send them round a loop they cannot get out of.
 */
export const EMAIL_UNVERIFIED_MESSAGE =
  'Confirm your email address before signing in. Check your inbox for the link we sent you.';

/**
 * Is this the refusal above? The client branches on it to draw the resend control.
 *
 * An exact comparison rather than a substring test: the message is a constant both halves
 * import, so anything else on this error is a different failure and must not be dressed up as
 * this one.
 */
export function isEmailUnverifiedMessage(message: string | null | undefined): boolean {
  return message === EMAIL_UNVERIFIED_MESSAGE;
}

/** The client route the link in a confirmation mail lands on. The token rides `?token=`. */
export const VERIFY_EMAIL_PATH = '/verify-email';

/** The query parameter that route reads the token out of. */
export const VERIFY_EMAIL_TOKEN_PARAM = 'token';

/**
 * How long a confirmation link is good for.
 *
 * A day: long enough to survive a mail that lands overnight or in a spam folder somebody checks
 * the next morning, short enough that a link forwarded or left in a shared inbox stops being a
 * key to the account fairly soon. Expiry is recoverable in one click — `auth.resendVerification`
 * — which is what lets this be short rather than generous.
 */
export const EMAIL_VERIFICATION_TTL_HOURS = 24;
export const EMAIL_VERIFICATION_TTL_MS = EMAIL_VERIFICATION_TTL_HOURS * 60 * 60 * 1000;

/**
 * Why a token was refused. One value per thing a player can be told, because "that link did not
 * work" is the message that generates a support ticket:
 *
 * - `expired` — it was real and is past `EMAIL_VERIFICATION_TTL_HOURS`. Offer another.
 * - `used` — this exact link was already consumed. Almost always a second click on the same
 *   mail, so the honest answer is "you are already verified", not an error.
 * - `invalid` — no such token. A truncated link, a mangled one, or a guess.
 */
export const VERIFY_EMAIL_FAILURES = ['expired', 'used', 'invalid'] as const;

export type VerifyEmailFailure = (typeof VERIFY_EMAIL_FAILURES)[number];
